import React, { useEffect, useMemo, useState } from "react";
import { format, isSameMonth } from "date-fns";
import EquipmentService from "../../../services/Equipment.service";
import EquipmentCalendarGrid from "./EquipmentCalendarGrid";

export default function EquipmentMonthlyCalendar({
    equipmentTypeId,
    equipmentList,
    refreshSignal,
    onStartCheck,
    onOpenDetails,
    onResolveIssue,
}) {
    const today = new Date();

    const [currentYear, setCurrentYear] = useState(today.getFullYear());
    const [inspections, setInspections] = useState([]);
    const [issues, setIssues] = useState([]);
    const [loading, setLoading] = useState(false);

    const months = useMemo(
        () => Array.from({ length: 12 }, (_, i) => new Date(currentYear, i, 1)),
        [currentYear]
    );

    useEffect(() => {
        if (!equipmentTypeId) return;
        setLoading(true);
        Promise.all([
            EquipmentService.listInspections({
                equipment_type: equipmentTypeId,
                year: currentYear,
            }),
            EquipmentService.listIssues({ equipment_type: equipmentTypeId, is_resolved: "false" }),
        ])
            .then(([inspRes, issueRes]) => {
                setInspections(Array.isArray(inspRes.data) ? inspRes.data : inspRes.data.results || []);
                setIssues(Array.isArray(issueRes.data) ? issueRes.data : issueRes.data.results || []);
            })
            .finally(() => setLoading(false));
    }, [equipmentTypeId, currentYear, refreshSignal]);

    const goBack = () => setCurrentYear((y) => y - 1);
    const goForward = () => setCurrentYear((y) => y + 1);

    const isCurrentYear = currentYear >= today.getFullYear();
    const currentMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);

    const columns = useMemo(
        () =>
            months.map((monthDate) => ({
                key: monthDate.toISOString(),
                label: format(monthDate, "MMM"),
                isCurrent: isSameMonth(monthDate, today),
                isPast: monthDate.getTime() < currentMonthStart.getTime(),
                monthDate,
            })),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [months]
    );

    const inspectionFor = (equipmentId, columnKey) =>
        inspections.find(
            (i) =>
                i.equipment === equipmentId &&
                isSameMonth(new Date(`${i.date}T00:00:00`), new Date(columnKey))
        );

    const openIssueFor = (equipmentId) => issues.find((i) => i.equipment === equipmentId) || null;

    const handleUncheck = async (inspection) => {
        setLoading(true);
        try {
            await EquipmentService.deleteInspection(inspection.id);
            setInspections((prev) => prev.filter((i) => i.id !== inspection.id));
        } finally {
            setLoading(false);
        }
    };

    return (
        <EquipmentCalendarGrid
            title={format(new Date(currentYear, 0, 1), "yyyy")}
            onPrev={goBack}
            onNext={goForward}
            nextDisabled={isCurrentYear}
            columns={columns}
            equipmentList={equipmentList}
            loading={loading}
            inspectionFor={inspectionFor}
            openIssueFor={openIssueFor}
            onStartCheck={onStartCheck}
            onOpenDetails={onOpenDetails}
            onResolveIssue={onResolveIssue}
            onUncheck={handleUncheck}
        />
    );
}
